/**
 * Kunden-Seite: Ticket ueber Token-Link anlegen.
 * Kein Login. Token aus der URL wird an Backend gereicht — der Portal-Public-Proxy
 * leitet an den Service-Desk-Container mit X-API-Key durch.
 */
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Headphones, CheckCircle2 } from 'lucide-react';

interface CreateResponse {
  success: boolean;
  data?: { ticket_number: string; view_token: string };
  error?: string;
}

export default function PublicCreateTicketPage() {
  const { token } = useParams<{ token: string }>();
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<{ ticket_number: string; view_url: string } | null>(null);

  const submit = async () => {
    if (!token) { setError('Kein Token in der URL'); return; }
    if (!subject.trim() || !description.trim()) { setError('Betreff und Beschreibung sind Pflichtfelder'); return; }
    setSubmitting(true); setError('');
    try {
      const r = await fetch('/api/public/service-desk/create-ticket', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          subject: subject.trim(),
          description: description.trim(),
          category: category.trim() || null,
        }),
      });
      const j = (await r.json()) as CreateResponse;
      if (!r.ok || !j.success || !j.data) {
        setError(j.error ?? `Fehler ${r.status}`);
        return;
      }
      setResult({
        ticket_number: j.data.ticket_number,
        view_url: `${window.location.origin}/ticket/${j.data.view_token}`,
      });
    } catch (err) {
      setError((err as Error).message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-start justify-center py-12 px-4">
      <div className="w-full max-w-lg">
        <div className="flex items-center gap-2 mb-6">
          <Headphones size={22} className="text-brand-400" />
          <h1 className="text-xl font-semibold text-slate-100">Service-Anfrage</h1>
        </div>

        {result ? (
          <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-5 space-y-3">
            <div className="flex items-center gap-2 text-emerald-300">
              <CheckCircle2 size={20} />
              <span className="font-medium">Ticket angelegt: {result.ticket_number}</span>
            </div>
            <p className="text-sm text-slate-200">
              Wir haben Ihre Anfrage aufgenommen und melden uns per Mail bei Ihnen.
            </p>
            <p className="text-xs text-slate-400">
              Sie koennen den Bearbeitungsstand jederzeit hier abrufen:
            </p>
            <a
              href={result.view_url}
              className="block text-xs text-brand-300 hover:text-brand-200 break-all"
            >
              {result.view_url}
            </a>
          </div>
        ) : (
          <div className="rounded-lg border border-slate-800 bg-slate-900 p-5 space-y-4">
            <p className="text-sm text-slate-300">
              Bitte beschreiben Sie Ihr Anliegen. Wir bearbeiten Ihre Anfrage schnellstmoeglich.
            </p>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Betreff *</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Kurze Zusammenfassung"
                className="w-full h-10 px-3 rounded-md border border-slate-700 bg-slate-950 text-slate-100"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Beschreibung *</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={6}
                placeholder="Was ist passiert? Welche Auswirkung hat es?"
                className="w-full px-3 py-2 rounded-md border border-slate-700 bg-slate-950 text-slate-100 text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Kategorie (optional)</label>
              <input
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="z.B. Stoerung, Frage, Bestellung"
                className="w-full h-9 px-3 rounded-md border border-slate-700 bg-slate-950 text-slate-100 text-sm"
              />
            </div>
            {error && <div className="text-sm text-rose-400">{error}</div>}
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-md bg-brand-600 text-primary-foreground hover:bg-brand-500 disabled:opacity-50"
            >
              {submitting && <Loader2 size={16} className="animate-spin" />}
              Anfrage absenden
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
